import React, { createContext, useContext, useState } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    UfoCustomTimingKey,
    ufoEventOnboardingInviteUsers
} from "@/components/signup/utils";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";

import { useCloudStatus } from "../../../../../../hooks";
import { ANALYTICS_PREFIX } from "../../../../common/constants";

import { Fetched } from "./types";
import {
    ProjectRoleIds,
    useJfind1663FetchProjectRoleIds
} from "./use-jfind-1663-fetch-project-role-ids";

const PROJECT_CREATE_TASK_POLLING_TIMEOUT = 17000;
const CHECK_TASK_ID_INTERVAL = 500;

interface CreateProjectTaskPollingContext {
    projectId: Fetched<string>;
    projectRoleIds: Fetched<ProjectRoleIds>;
    setTaskId: React.Dispatch<React.SetStateAction<Fetched<string>>>;
    taskId: Fetched<string>;
    pollTaskByAwaitedTaskId: () => Promise<boolean>;
}

const DEFAULT = {
    taskId: undefined,
    projectId: undefined,
    projectRoleIds: undefined,
    setTaskId: () => undefined,
    pollTaskByAwaitedTaskId: () => Promise.resolve(false)
} satisfies CreateProjectTaskPollingContext;

const CreateProjectTaskPollingContext =
    createContext<CreateProjectTaskPollingContext>(DEFAULT);

export const Jfind1663CreateProjectTaskPollingProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const stargateClient = useBxpWebStargateClient();
    const { cloudId } = useCloudStatus();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { fetchRoles } = useJfind1663FetchProjectRoleIds();

    const [taskId, setTaskId] = useState<Fetched<string>>(undefined);
    const [hasPolled, setHasPolled] = useState(false);

    const [projectId, setProjectId] = useState<Fetched<string>>(null);

    const [projectRoleIds, setProjectRoleIds] =
        useState<Fetched<ProjectRoleIds>>(null);

    const pollTaskByAwaitedTaskId = async (): Promise<boolean> => {
        if (taskId === null) {
            // if we have resolved to null, the task creation has failed
            await Promise.resolve(
                createAnalyticsEvent({
                    action: "failed",
                    actionSubject: "polling",
                    actionSubjectId: `${ANALYTICS_PREFIX}InviteCreateProjectTaskPolling`,
                    eventType: "track",
                    attributes: {
                        taskId: null
                    }
                }).fire()
            );
            return false;
        }

        if (hasPolled) {
            // prevent double polling
            return false;
        }

        if (taskId === undefined) {
            // if we haven't resolved a taskId yet, wait and try again
            await new Promise((resolve) =>
                setTimeout(resolve, CHECK_TASK_ID_INTERVAL)
            );
            return pollTaskByAwaitedTaskId();
        }

        try {
            setHasPolled(true);

            ufoEventOnboardingInviteUsers.startSubstep(
                UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_POLLING_PROJECT_CREATE_TASK
            );

            const { status, result } =
                await stargateClient.pollTaskUntilComplete(
                    taskId,
                    cloudId,
                    PROJECT_CREATE_TASK_POLLING_TIMEOUT
                );

            if (status === "COMPLETE") {
                const projectId = result?.projectId;

                setProjectId(projectId);

                ufoEventOnboardingInviteUsers.endSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_POLLING_PROJECT_CREATE_TASK
                );
                ufoEventOnboardingInviteUsers.startSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_FETCHING_ROLES
                );

                const roleIds = await fetchRoles(cloudId, projectId);
                setProjectRoleIds(roleIds);

                ufoEventOnboardingInviteUsers.endSubstep(
                    UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_FETCHING_ROLES
                );
            } else {
                setProjectId(null);

                await Promise.resolve(
                    createAnalyticsEvent({
                        action: "failed",
                        actionSubject: "polling",
                        actionSubjectId: `${ANALYTICS_PREFIX}InviteCreateProjectTaskPolling`,
                        eventType: "track",
                        attributes: {
                            taskStatus: status,
                            taskId
                        }
                    }).fire()
                );
            }
        } catch (error) {
            setProjectId(null);
        }

        // once we have resolved our polling, resolve the promise to unblock submission
        return true;
    };

    return (
        <CreateProjectTaskPollingContext.Provider
            value={{
                projectId,
                projectRoleIds,
                setTaskId,
                taskId,
                pollTaskByAwaitedTaskId
            }}
        >
            {children}
        </CreateProjectTaskPollingContext.Provider>
    );
};

export const useJfind1663TaskPolling = (): CreateProjectTaskPollingContext => {
    const context = useContext(CreateProjectTaskPollingContext);
    if (!context) {
        // Do not throw error to enable harmless usage when unenrolled
        return DEFAULT;
    }

    return context;
};
